export type AccentTone = "ember" | "steel" | "acid" | "blood";

export type NavigationItem =
  | {
      kind: "route";
      to: string;
      label: string;
    }
  | {
      kind: "anchor";
      href: string;
      label: string;
    };

export const primaryNavigation: NavigationItem[] = [
  { kind: "route", to: "/play", label: "Play" },
  { kind: "route", to: "/analysis", label: "Analysis" },
  { kind: "route", to: "/livestream", label: "Livestream" },
  // { kind: "route", to: "/games", label: "Games" },
  // { kind: "route", to: "/blogs", label: "Community" },
];

export const secondaryNavigation: NavigationItem[] = [
  { kind: "route", to: "/devlog", label: "Dev log" },
  { kind: "route", to: "/about", label: "About" },
];

export const authAction = { to: "/signup", label: "Sign up" };

export const footerLinks = [
  { href: "/about", label: "About the model" },
  { href: "/blogs", label: "Developer blog" },
  { href: "/login", label: "Sign in" },
];

export const homeHero = {
  title: "Catie Chess",
  statement: "Chess is Language",
  subtitle: "Sequence-based. No search.",
  pillars: ["Human-like moves.", "Human-like plans.", "Human-like mistakes."],
} as const;

export const moveFeed = [
  { move: "1. e4", side: "Human", note: "Space first. The user opens with intent." },
  { move: "... c5", side: "Model", note: "The model answers with asymmetry." },
  { move: "2. Nf3", side: "Human", note: "Development stays practical, not decorative." },
  { move: "... d6", side: "Model", note: "Structure is preserved for a real fight." },
  { move: "3. d4", side: "Human", note: "Pressure arrives before the middlegame." },
];

export const demoBoardMoves = [
  "e2e4",
  "c7c5",
  "g1f3",
  "d7d6",
  "d2d4",
];

export const lichessBoardPalette = {
  light: "#f0d9b5",
  dark: "#b58863",
  lastMove: "rgba(255, 235, 59, 0.30)",
  legalTarget: "radial-gradient(circle, rgba(32, 120, 39, 0.72) 0 28%, transparent 30%)",
  selected: "inset 0 0 0 5px rgba(32, 120, 39, 0.92)",
  check: "inset 0 0 0 3px rgba(199, 60, 32, 0.9)",
};

export const homeFaqItems = [
  {
    title: "What is Catie?",
    body: [
      "Catie is a GPT-style chess AI that plays without engine search.",
      "Like a language model predicts the next word, Catie predicts the next chess move as a token from the moves that came before.",
      "No Stockfish.",
      "No tree search.",
      "No looking millions of positions ahead.",
      "Just sequence prediction.",
    ],
  },
  {
    title: "How does Catie play chess?",
    body: [
      "Catie reads a game as a sequence: move -> move -> move -> next move.",
      "Instead of evaluating only the current board, it learns patterns from the full history of the game: openings, plans, mistakes, style, and momentum.",
      "That means Catie does not choose the best engine move.",
      "It generates the next move directly from what it has learned.",
    ],
  },
  {
    title: "Does Catie calculate like a normal chess engine?",
    body: [
      "No - not in the engine sense.",
      "Traditional engines search many possible futures before choosing a move.",
      "Catie does not run engine search, does not use Stockfish, and does not look ahead through move trees at play time.",
      "It plays by neural prediction: given this game so far, what move comes next?",
    ],
  },
  {
    title: "How is Catie trained?",
    body: [
      "Catie starts with one base model trained on chess games written as move-token sequences.",
      "Then we use SFT - supervised fine-tuning - to create different versions of Catie with different behaviors.",
      "For example, we can fine-tune models to play more aggressively, more solidly, more beginner-like, more master-like, or with different styles and personalities.",
      "Same base model. Different fine-tuned players.",
    ],
  },
  {
    title: "Join the experiment",
    body: [
      "Play against Catie. Find weird moves. Share your games. Compare different versions.",
      "Help us test what GPT-style chess models can and cannot do.",
      "Join our Discord to follow new models, experiments, and community challenges.",
    ],
  },
] as const;

export const homeCommunityInvite = {
  href: "https://discord.gg/uZqXYX77XX",
  label: "Join Discord",
  eyebrow: "Community server",
  detail: "New Catie models, experiments, and challenges.",
} as const;

export const authorityCards = [
  {
    value: "MOST",
    title: "Human-like is the visible thesis, not a buried research footnote.",
    detail:
      "The homepage should state the claim with confidence before the rest of the product explains why it is earned.",
  },
  {
    value: "LIVE",
    title: "A public livestream board makes the site feel inhabited in real time.",
    detail:
      "Livestream-guess is not decoration. It gives motion to the brand and proves that the site is alive before a user ever logs in.",
  },
  {
    value: "ASYNC",
    title: "Heavy model work should look like a serious system, not fake instant magic.",
    detail:
      "Queues, task states, and staged delivery are part of the product language. The interface should acknowledge real compute weight.",
  },
];

export const routeHighlights = [
  {
    accent: "ember" as const,
    eyebrow: "Play",
    title: "A match ritual for selecting time control, model identity, hardness, and opening drill.",
    body: "The play route should feel like entering a chamber. Setup is part of the drama, not dead admin UI.",
    href: "/play",
  },
  {
    accent: "steel" as const,
    eyebrow: "Analysis",
    title: "A command rail for board study, eval signals, Stockfish, and model move inspection.",
    body: "The analysis route needs enough sharpness to hold multiple systems at once without turning muddy.",
    href: "/analysis",
  },
  {
    accent: "acid" as const,
    eyebrow: "Games",
    title: "A dedicated arena for choose-move, human-vs-AI tests, and guess-the-elo modes.",
    body: "Mini-games belong in a controlled hub so the homepage remains concentrated and imperial.",
    href: "/games",
  },
  {
    accent: "blood" as const,
    eyebrow: "Community",
    title: "Profiles, awards, and blogs make the product feel inhabited by loyalists, not anonymous traffic.",
    body: "Developer posts, user posts, and public dossiers turn activity into durable presence.",
    href: "/blogs",
  },
];

export const playModels = [
  "2.2 base",
  "Hikaru",
  "2400-2900",
  "2500-2900",
  "2600-2900",
  "2700-2900",
];

export const gameModes = [
  {
    eyebrow: "What will you play?",
    title: "Choose one move, then compare your instinct with the model and Stockfish.",
    body: "A direct duel of taste. Fast to enter, immediate to score, easy to replay.",
  },
  {
    eyebrow: "Are you more human than AI?",
    title: "Ten positions. Human move versus model move. Accuracy, blunders, and overlap.",
    body: "A structured challenge mode that turns intuition into measurable identity.",
  },
  {
    eyebrow: "Choose move",
    title: "Take a position and pick the best move from a shuffled top-four shortlist.",
    body: "This mode should feel crisp and tactical, almost like a firing range for positional judgment.",
  },
  {
    eyebrow: "Guess the Elo",
    title: "Judge human games, bot games, and livestream positions by rating strength.",
    body: "Part scouting, part theater. The result should feed history, streaks, and public reputation.",
  },
];

export const communityTracks = [
  {
    title: "Developer blog / intro",
    body: "Big editorial posts that explain model goals, training logic, and site direction with clear authorship.",
  },
  {
    title: "Developer blog / update",
    body: "Sharper status posts about deployments, new weights, new games, and product changes.",
  },
  {
    title: "User blog",
    body: "Player-authored posts that make the domain feel inhabited and worth returning to.",
  },
];

export const aboutPillars = [
  {
    eyebrow: "Why different",
    title: "Human-likeness is treated as a front-facing product doctrine rather than a hidden training detail.",
    body: "The site should continuously explain why that matters in play, analysis, and community surfaces.",
  },
  {
    eyebrow: "How trained",
    title: "Model identity should feel plural: different style families, different strength bands, different conditions.",
    body: "The interface should present models like dangerous personalities, not sterile checkpoint names.",
  },
  {
    eyebrow: "Why activity matters",
    title: "Every played move, guessed stream, and public post should feed a lasting sense of rank.",
    body: "This is why the site can sustain more than a board widget. It accumulates history and identity.",
  },
];

export const profileAwards = [
  "Beat Hikaru mode 12 times",
  "Livestream guess streak: 18",
  "Guess-the-elo accuracy within 50: 41",
  "Community post reach: 6.2k reads",
];

export const linkedAccounts = ["lichess.org/catie", "chess.com/catie", "FIDE 2600+", "USCF linked"];
